'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';

import { atom, useAtom } from 'jotai';
import { useTranslations } from 'use-intl';

import { iFrameUrlAtom, mobileAppEmbedUrlAtom } from '@/shared-survey';
import { type InitiateSurveyV3Mutation } from '@/survey-graphql';

import {
  initializeIframeUrlIfUnset,
  initializeMobileAppEmbedUrlIfUnset,
} from './project-response-shared-effects.helpers';
import { MessageFormValues } from './project-response.hooks';
import { useRecordingDurationUpdates } from './project-usability-recording-chunk-duration.hooks';
import { useRecordingManager } from './project-usability-recording-manager.hooks';
import { stateManager } from './project-usability-recording-state-manager';
import { getServerRecordingSnapshot } from './project-usability-recording-state.helpers';
import { useUploadManager } from './project-usability-recording-upload.hooks';
import { getWidgetUtRecordingTimeSec } from './project-usability-widget-recording';

interface UseUsabilityProjectResponseProps {
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  handleSubmit: (values: MessageFormValues) => Promise<void>;
}

const sidebarOpenState = atom<boolean>(true);

export function useUsabilitySidebarState() {
  const [isOpen, setIsOpen] = useAtom(sidebarOpenState);

  const openSidebar = () => setIsOpen(true);
  const closeSidebar = () => setIsOpen(false);
  const toggleSidebar = () => setIsOpen(!isOpen);

  return {
    isOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
  };
}

export function useUsabilityProjectResponse({
  firstQuestion,
  handleSubmit,
}: UseUsabilityProjectResponseProps) {
  const [contentUrl, setIFrameUrl] = useAtom(iFrameUrlAtom);
  const [mobileAppEmbedUrl, setMobileAppEmbedUrl] = useAtom(
    mobileAppEmbedUrlAtom
  );
  const { openSidebar } = useUsabilitySidebarState();

  useEffect(
    () =>
      initializeIframeUrlIfUnset({
        current: contentUrl,
        candidate: firstQuestion?.contentUrl,
        set: setIFrameUrl,
      }),
    [contentUrl, firstQuestion, setIFrameUrl]
  );

  useEffect(
    () =>
      initializeMobileAppEmbedUrlIfUnset({
        current: mobileAppEmbedUrl,
        candidate: firstQuestion?.mobileAppEmbedUrl,
        set: setMobileAppEmbedUrl,
      }),
    [mobileAppEmbedUrl, firstQuestion, setMobileAppEmbedUrl]
  );

  const handleSubmitWithSidebar = async (values: {
    content: string;
    transcript?: string;
  }) => {
    if (handleSubmit) {
      await handleSubmit(values);
      openSidebar();
    }
  };

  return {
    contentUrl,
    setIFrameUrl,
    mobileAppEmbedUrl,
    setMobileAppEmbedUrl,
    handleSubmitWithSidebar: handleSubmitWithSidebar,
  };
}

export function useUsabilityRecordingDuration() {
  return {
    getCurrentRecordingTime: () =>
      getWidgetUtRecordingTimeSec() ?? stateManager.getCurrentRecordingTime(),
  };
}

interface UseUsabilityRecordingOptions {
  insightId: string | null;
  isAiVoiceEnabled?: boolean;
  skipLocalMic?: boolean;
}

export function useUsabilityRecording({
  insightId,
  isAiVoiceEnabled = false,
  skipLocalMic = false,
}: UseUsabilityRecordingOptions) {
  const t = useTranslations('Usability');

  const [manualError, setManualError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const {
    isPaused,
    isRecording,
    isUploadStalled,
    isPermissionDenied,
    isScreenShareMuted,
    isRequestingPermission,
    isFinalizing: isFinalizingRecording,
  } = useSyncExternalStore(
    stateManager.subscribe,
    stateManager.getSnapshot,
    getServerRecordingSnapshot
  );

  const [lastIsFinalizingRecording, setLastIsFinalizingRecording] = useState(
    isFinalizingRecording
  );

  const { handleChunkUploaded, resetTracker, updateFinalDuration } =
    useRecordingDurationUpdates({
      insightId,
      isRecording,
    });

  const handleChunk = (chunk: Blob) => {
    stateManager.addChunk(chunk, handleChunkUploaded);
  };

  const handleRecordingComplete = async (duration: number | null) => {
    try {
      await updateFinalDuration(duration);
    } catch {
      setManualError(t('updateRecordingDurationError'));
    }
  };

  const { error: uploadError } = useUploadManager({ insightId });
  const {
    error: recordingError,
    start,
    stop,
  } = useRecordingManager({
    onChunk: handleChunk,
    onRecordingComplete: handleRecordingComplete,
    isAiVoiceEnabled,
    insightId,
    skipLocalMic,
  });

  useEffect(() => {
    if (isRecording) {
      resetTracker();
    }
  }, [isRecording, resetTracker]);

  if (lastIsFinalizingRecording !== isFinalizingRecording) {
    setLastIsFinalizingRecording(isFinalizingRecording);
    if (!isFinalizingRecording) setUploadProgress(null);
  }

  useEffect(() => {
    if (!isFinalizingRecording) return;

    const tick = () => {
      setUploadProgress(stateManager.getUploadProgress());
    };

    const initialTimeout = setTimeout(tick, 0);
    const interval = setInterval(tick, 500);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [isFinalizingRecording]);

  const error = manualError ?? uploadError ?? recordingError;

  const resetPermissions = () => {
    stateManager.setPermissionDenied(false);
    setManualError(null);
  };

  return {
    isRecording,
    isFinalizingRecording,
    isUploadStalled,
    retryUpload: () => stateManager.retryUpload(),
    uploadProgress,
    error,
    start,
    stop,
    endRecording: () => {
      stateManager.endRecording();
    },
    isPermissionDenied,
    isScreenShareMuted,
    isRequestingPermission,
    resetPermissions,
    getCurrentRecordingTime: () => stateManager.getCurrentRecordingTime(),
    getExactDuration: () => stateManager.getExactDuration(),
    updateActivity: () => stateManager.updateActivity(),
    getRemainingTime: () => stateManager.getRemainingTime(),
    isTimedOut: () => stateManager.isTimedOut(),
    pauseRecording: (reason?: string) => stateManager.pauseRecording(reason),
    resumeRecording: (reason?: string) => stateManager.resumeRecording(reason),
    isPaused,
  };
}
